import { MaskedValue } from '@/components';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { cn } from '@/lib';
import { useTranslation } from 'react-i18next';
import type { TheftProtection } from '../../types';

export interface TheftProtectionCardProps {
  theftProtection: TheftProtection;
}

// TheftProtectionCard shows whether theft protection is on and, only while active, the lock
// duration and the masked unlock PIN.
export function TheftProtectionCard({ theftProtection }: TheftProtectionCardProps) {
  const { t } = useTranslation();
  const statusKey = theftProtection.active ? 'active' : 'inactive';

  return (
    <PageContentCard className="w-full">
      {/* Title and description left, status badge right */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-onsurface text-lg font-semibold">{t('devices.detail.rules.theftProtection.title')}</h2>
          <p className="text-muted max-w-prose text-sm">{t('devices.detail.rules.theftProtection.description')}</p>
        </div>
        <span
          className={cn(
            'rounded-md border px-3 py-1 text-xs whitespace-nowrap',
            theftProtection.active ? 'border-success/20 bg-success/10 text-success' : 'border-border text-muted'
          )}
        >
          {t(`devices.detail.rules.theftProtection.status.${statusKey}`)}
        </span>
      </div>

      {theftProtection.active && <ActiveProtectionDetails theftProtection={theftProtection} />}
    </PageContentCard>
  );
}

// ActiveProtectionDetails renders duration and PIN, which only exist while protection is active.
function ActiveProtectionDetails({ theftProtection }: { theftProtection: TheftProtection }) {
  const { t } = useTranslation();
  const durationLabel = theftProtection.duration
    ? t(`devices.detail.rules.theftProtection.duration.${theftProtection.duration}`)
    : t('common.notSet');

  return (<div className="border-border mt-5 border-t pt-5">
    <div className="mx-auto flex w-fit max-w-full flex-wrap items-start justify-start gap-8">
      {/* Lock duration */}
      <div>
        <div className="text-muted mb-2 text-xs">{t('devices.detail.rules.theftProtection.duration.label')}</div>
        <div className="text-onsurface text-sm font-medium">{durationLabel}</div>
      </div>
      {/* Unlock PIN */}
      <div>
        <div className="text-muted mb-2 text-xs">{t('devices.detail.rules.theftProtection.pin')}</div>
        <MaskedValue value={theftProtection.unlockPin} />
      </div>
    </div>
  </div>
  );
}
